import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from "motion/react";
import { BookOpen, ArrowRight } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { PageHero } from '../../components/common/PageHero';

interface CourseDto {
    id: string;
    slug: string;
    icon?: string;
    imageUrl?: string;
    color?: string;
    title: string;
    description: string;
}

export const Kursangebot: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Fetch page metadata and update title
    const { data: pageMeta } = usePageMetadata('kursangebot');

    const { data: pagedData, isLoading } = useQuery<{ data: CourseDto[] }>({
        queryKey: ['publicCourses'],
        queryFn: async () => {
            const response = await apiClient.get('/public/courses');
            return response.data;
        }
    });

    const courses = pagedData?.data || [];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <div className="flex flex-col bg-background min-h-screen">
            {/* Subpage Hero with Cover Photo behind Title */}
            <PageHero
                title={pageMeta?.title || t('courses.title')}
                subtitle={pageMeta?.metaDescription || t('courses.description')}
                breadcrumb={[
                    { label: t('common.home'), to: '/' },
                    { label: pageMeta?.title || t('courses.breadcrumb') }
                ]}
            />

            <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 w-full py-16 sm:py-24">
                {/* Course List: One by one (Tek Tek, Grid Değil) */}
                {isLoading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                    </div>
                ) : courses && courses.length > 0 ? (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        className="space-y-8"
                    >
                        {courses.map((course: CourseDto) => (
                            <motion.div
                                key={course.id}
                                variants={itemVariants}
                                onClick={() => navigate(`/kurse/${course.slug}`)}
                                className="group relative flex flex-col md:flex-row items-stretch justify-between rounded-3xl border border-border bg-background shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg cursor-pointer overflow-hidden"
                            >
                                <div className="w-full md:w-80 h-56 md:h-auto shrink-0 relative overflow-hidden bg-primary/5 flex items-center justify-center">
                                    {course.imageUrl ? (
                                        <img src={resolveAssetUrl(course.imageUrl)} alt={course.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    ) : (
                                        <BookOpen className="h-16 w-16 text-primary" style={course.color ? { color: course.color } : undefined} />
                                    )}
                                </div>
                                <div className="flex flex-1 flex-col justify-between p-6 sm:p-8">
                                    <div>
                                        <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                                            {course.title}
                                        </h3>
                                        <div className="mt-4 text-base leading-relaxed text-foreground/80 line-clamp-3">
                                            {course.description}
                                        </div>
                                    </div>
                                    <div className="mt-8 flex items-center justify-end w-full border-t border-border/50 pt-4">
                                        <span className="text-sm font-semibold text-primary group-hover:text-primary-foreground group-hover:bg-primary px-4 py-2 rounded-lg transition-colors border border-transparent group-hover:border-primary flex items-center gap-2">
                                            {t('courses.learn_more')} <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="rounded-3xl border border-dashed border-border bg-secondary/30 p-16 text-center">
                        <h3 className="text-lg font-medium text-foreground">{t('courses.no_courses')}</h3>
                        <p className="mt-2 text-foreground/60">{t('courses.check_back')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
